import { useEffect, useState } from "react";
import { api, apiErrorMessage } from "@/lib/api";
import { useProperty } from "@/contexts/PropertyContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Bot, Save } from "lucide-react";

/**
 * AutoPickSettingsCard — per-property toggle that lets the admin stop
 * babysitting maintenance bids. When N vendors have submitted, the system
 * auto-accepts the cheapest (with an optional max-amount cap for safety).
 */
export default function AutoPickSettingsCard() {
  const { propertyId, currentProperty } = useProperty();
  const [enabled, setEnabled] = useState(false);
  const [minBids, setMinBids] = useState(3);
  const [maxAmount, setMaxAmount] = useState("");
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!propertyId) return;
    (async () => {
      try {
        const { data } = await api.get("/admin/tickets/auto-pick", { params: { property_id: propertyId } });
        setEnabled(!!data.enabled);
        setMinBids(data.min_bids || 3);
        setMaxAmount(data.max_amount != null ? String(data.max_amount) : "");
        setLoaded(true);
      } catch (e) { /* silent — treated as defaults */ setLoaded(true); }
    })();
  }, [propertyId]);

  const save = async () => {
    if (!propertyId) return toast.error("Pick a property first");
    setSaving(true);
    try {
      const payload = {
        property_id: propertyId,
        enabled,
        min_bids: Math.max(2, Math.min(20, Number(minBids) || 3)),
        max_amount: maxAmount === "" ? null : Number(maxAmount),
      };
      await api.put("/admin/tickets/auto-pick", payload);
      toast.success(enabled ? "Auto-pick armed" : "Auto-pick disabled");
    } catch (e) { toast.error(apiErrorMessage(e)); }
    setSaving(false);
  };

  return (
    <Card className="border-white/10 bg-card p-4 sm:p-5 mb-4" data-testid="auto-pick-card">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-3 min-w-0 flex-1">
          <div className="h-9 w-9 rounded-md bg-indigo-500/10 border border-indigo-500/30 flex items-center justify-center shrink-0">
            <Bot className="h-4 w-4 text-indigo-300" />
          </div>
          <div className="min-w-0">
            <div className="overline">Ops automation</div>
            <div className="text-lg font-semibold font-display mt-1 flex items-center gap-2">
              Auto-pick cheapest bid
              {enabled && (
                <Badge variant="outline" className="border-emerald-500/40 text-emerald-300 bg-emerald-500/10 text-[10px] uppercase tracking-widest" data-testid="auto-pick-armed">
                  Armed
                </Badge>
              )}
            </div>
            <div className="text-xs text-zinc-500 mt-1">
              Once <b>{minBids}</b> vendors have bid on a ticket in {currentProperty?.name || "this property"}, the system picks the lowest and assigns them automatically{maxAmount ? ` — as long as the bid is ≤ $${maxAmount}` : ""}.
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <Switch checked={enabled} onCheckedChange={setEnabled} disabled={!loaded} data-testid="auto-pick-switch" />
          <Button size="sm" onClick={save} disabled={saving || !loaded} className="bg-orange-500 hover:bg-orange-600" data-testid="auto-pick-save">
            <Save className="h-3.5 w-3.5 mr-1" /> {saving ? "Saving…" : "Save"}
          </Button>
        </div>
      </div>

      {enabled && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4 pt-4 border-t border-white/5">
          <div>
            <Label className="text-xs text-zinc-400">Trigger after N vendors bid</Label>
            <Input
              type="number"
              min={2}
              max={20}
              value={minBids}
              onChange={(e) => setMinBids(e.target.value)}
              className="bg-white/[0.02] border-white/10 mt-1"
              data-testid="auto-pick-min-bids"
            />
            <div className="text-[10px] text-zinc-500 mt-1">Between 2 and 20 bids.</div>
          </div>
          <div>
            <Label className="text-xs text-zinc-400">Safety cap — max amount (optional)</Label>
            <Input
              type="number"
              min={0}
              step="0.01"
              placeholder="e.g. 500.00"
              value={maxAmount}
              onChange={(e) => setMaxAmount(e.target.value)}
              className="bg-white/[0.02] border-white/10 mt-1"
              data-testid="auto-pick-max-amount"
            />
            <div className="text-[10px] text-zinc-500 mt-1">Skip auto-pick if the cheapest bid is higher than this. Blank = no cap.</div>
          </div>
        </div>
      )}
    </Card>
  );
}
